import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import { Collapse } from 'antd';
import FilterComponent from '../../../children/FilterComponent/FilterComponent';
import { FEEDBACK_360_TYPE_NON_COHORT } from '../../../../../../../config/questionTypeConstants';
import { BUTTON_NAME, HEADER_TEXT } from './constants';
import { buildBenchmarkFiltersPayload } from '../../utils/buildBenchmarkFiltersPayload';
import { getDataFromUrlToken } from '../../../../../../utils/urlDataStore';
import { URL_DATA } from '../../../../../../../config/urlDataConstants';
import { toast } from '../../../../../../utils/messageUtils';
import SectionHeader from '../SectionHeader';
import SVGIcons from '../../../../../../Components/SVGIcons';
import { MIN_LEADERS_REQUIRED } from '../InsufficientSelectionNotice/constants';
import { fetchTrendData } from '../TrendComparison/utils/fetchTrendData';


const FilterSection = (props) => {
    useEffect(() => {
        // surveys defaults to [] on first load, and the backend treats an empty
        // surveys filter as "no filter" (fetches every leader in the org), not
        // "zero leaders" — so this guard isn't just the confidentiality-suppression
        // check (see InsufficientSelectionNotice), it's also what stops the very
        // first mount from silently requesting the whole org's data.

        if (!props.isSurveyListFetched) {
            const orgDetails = getDataFromUrlToken(URL_DATA.ORGANIZATION);
            // getOrgRaterGroups and fetchSurveysFromTheOrg run in parallel; whether
            // to call the visualization APIs depends on both results, so track each
            // outcome and only decide once the second one has landed.
            let raterGroupsResult;
            let surveyListResult;

            const tryCallVisualizationApis = () => {
                if (raterGroupsResult === undefined || surveyListResult === undefined) return;

                const hasEnoughLeaders =
                    getSurveysCount(surveyListResult) > 10;

                if (hasEnoughLeaders && !props.isVisualizationDataFectched) {
                    callVisualizationApis()
                }
                else {
                    props.setInsufficientNotice(true)
                }
            };
            props.getOrgRaterGroups({}, (isSuccess, data) => {
                raterGroupsResult = { isSuccess, data };
                if (isSuccess) {
                    tryCallVisualizationApis();
                }

            })

            props.fetchSurveysFromTheOrg({ projectTypes: FEEDBACK_360_TYPE_NON_COHORT, size: orgDetails.projectCount + 10 }, (isSuccess, data) => {
                surveyListResult = { isSuccess, data };
                if (isSuccess) {
                    tryCallVisualizationApis();

                }
            })

        }
    }, [])

    // Applying while the comparison is on fetches the trend APIs only, so the
    // plain view behind it is still built from the filters before that. Turning
    // the comparison off is the moment that becomes visible, so it re-fetches
    // then — and only then: after an Apply with the comparison off, or a toggle
    // that changed no filters, this side is already current and nothing runs.
    // The first render is skipped because the mount effect above owns that
    // fetch, guarded by the survey list and the leader count.
    const isInitialRender = useRef(true);
    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }
        if (!props.isTrendComparison && !props.isBenchmarkDataFetched) {
            callVisualizationApis()
        }
    }, [props.isTrendComparison])

    const [collapseActiveKey, setCollapseActiveKey] = React.useState(['0']);

    const header = (
        <div
            className='filter-header'
        >
            <span className='filter-header__left'>
                <span className='filter-header__icon'>
                    <SVGIcons type='SVG-filter' />
                </span>
                <span className='filter-header__subhead'>Filters</span>
            </span>
            <span
                className='filter-header__sideopen'
            >
                {Array.isArray(collapseActiveKey) && collapseActiveKey.length > 0 ? 'Hide filters' : 'Show filters'}
            </span>
        </div >
    );

    const callVisualizationApis = () => {
        let benchmarkFilters = buildBenchmarkFiltersPayload(props.benchmarkFiltersAnalysis)
        if (props.isTrendComparison) {
            // Same set the comparison toggle fires, marked as an initial fetch:
            // new filters rebuild both lists from nothing, so the section goes
            // back to its full-width loader.
            // No pairing to pass: handleApply has just put it back to how it
            // starts, which is what the fetch falls back to. Reading it off props
            // would send the old one anyway — a dispatch doesn't reach props
            // until the next render.
            fetchTrendData(props)
        }
        else {
            props.getBenchmarkKpiTiles({ benchmarkFilters })
            props.getCantAnswerResponses({ benchmarkFilters })
            props.getDevAndStrengthVisualization({ benchmarkFilters })
            props.getTopStrengthTopDev({ benchmarkFilters })
            props.getTotalFeedbackProviders({ benchmarkFilters })
            props.getRaterHeatmap({ benchmarkFilters })
            props.getSelfVsOthers({ benchmarkFilters })
            props.getDistributionStrip({ benchmarkFilters })
            props.getBenchmarkAlignmentScore({ benchmarkFilters })
            props.getRankDelta({ benchmarkFilters })
            props.getBenchmarkTaxonomy({ benchmarkFilters })
            props.setBenchmarkDataFetched({ value: true })
        }



    }

    const handleApply = () => {
        if (props.benchmarkFiltersAnalysis && props.benchmarkFiltersAnalysis.surveys.length > 0) {
            setCollapseActiveKey([]);
            // Below the minimum leader count, InsufficientSelectionNotice takes over
            // the results area, so there's nothing to fetch yet.
            if (props.benchmarkFiltersAnalysis.surveys.length >= MIN_LEADERS_REQUIRED) {
                props.setInsufficientNotice(false)
                // Applying is the only thing that makes results out of date, and
                // it makes BOTH sides stale — callVisualizationApis then fetches
                // whichever side the comparison toggle is showing and marks that
                // one fresh again. The other stays stale until the toggle brings
                // it back on screen, which is what re-fetches it.
                props.setTrendDataFetched({ value: false })
                props.setBenchmarkDataFetched({ value: false })
                // An override names one leader's pair of 360s; the rebuilt lists
                // needn't hold that leader at all, so the pairing starts over.
                props.resetTrendPairing()
                callVisualizationApis()

            }
            else {
                props.setInsufficientNotice(true)
            }
        }
        else {
            props.setInsufficientNotice(true)
        }

    };


    const onCustomRangeChange = (range, date, dd) => {
        // When both dates are selected, set the custom range and clear any preset
        if (range && range[0] && range[1]) {
            props.handleChangeBenchmarkFilter({ changedItem: { date: { customDateRange: date, preset: "" } } })
            return;
        }

        // When cleared (RangePicker X button), reset the custom range and preset
        if (!range) {
            props.handleChangeBenchmarkFilter({ changedItem: { date: { customDateRange: [], preset: "" } } })
        }
    };

    const handleChangeFilterValues = (eventDetails) => {
        props.handleChangeBenchmarkFilter({ changedItem: eventDetails })


    }

    const getTotalProjectCount = () => {
        if (props.fetchSurveyLists.data && props.fetchSurveyLists.data.totalChildProjects != undefined) {
            if (props.fetchSurveyLists.data.totalNonCohortProjects != undefined) {
                return props.fetchSurveyLists.data.totalChildProjects + props.fetchSurveyLists.data.totalNonCohortProjects
            }
            return props.fetchSurveyLists.data.totalChildProjects
        }
        else return 0
    }


    const getSurveysCount = (surveyListResult) => {
        if (surveyListResult.data && surveyListResult.data.data && surveyListResult.data.data.totalChildProjects != undefined) {
            if (surveyListResult.data.data.totalNonCohortProjects != undefined) {
                return surveyListResult.data.data.totalChildProjects + surveyListResult.data.data.totalNonCohortProjects
            }
            return surveyListResult.data.data.totalChildProjects
        }
        else return 0

    }


    return (
        <>
            <SectionHeader
                title={HEADER_TEXT.title}
                description={HEADER_TEXT.description}
            />
            <div className='filter-sectionWrapper'>
            <Collapse
                expandIconPosition="right"
                activeKey={collapseActiveKey}
                onChange={setCollapseActiveKey}
            >
                <Collapse.Panel header={header} key="0">
                    <FilterComponent
                            isDateSectionHidden={props.isTrendComparison}
                            availableRaterGroups={props.fetchOrgRaterGroupsApi.data && props.fetchOrgRaterGroupsApi.data.ratersList ? props.fetchOrgRaterGroupsApi.data.ratersList : []}
                            isError={props.fetchSurveyLists.error || props.fetchOrgRaterGroupsApi.error}
                            loader={props.fetchSurveyLists.isProcessing || props.fetchOrgRaterGroupsApi.isProcessing}
                        surveyList={props.fetchSurveyLists.data && props.fetchSurveyLists.data.data ? props.fetchSurveyLists.data.data : []}
                        totalCount={getTotalProjectCount()}
                        buttonName={BUTTON_NAME}
                        handleChangeFilterValues={handleChangeFilterValues}
                        dateFields={props.benchmarkFiltersAnalysis.date}
                        surveyField={props.benchmarkFiltersAnalysis.surveys}
                        raterGroups={props.benchmarkFiltersAnalysis.raterGroups}
                        onCustomRangeChange={onCustomRangeChange}
                        surveyNames={props.benchmarkFiltersAnalysis.surveyNames}
                        handleApply={handleApply}
                    />
                </Collapse.Panel>
            </Collapse>
        </div>
        </>
    );
};

export default FilterSection;

